import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import {
  ScriptMarkupBreadcrumb,
  ScriptMarkupFaq,
  WIDTH_LADDERS,
  getCloudinarySrcSet,
  getCloudinaryUrl,
} from 'components/rich';
import useDataContext from 'data/context/data';
import contentApi from 'data/api/content';
import { getSkuProductPrices } from 'data/lib/product';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';
import { couponSale, discountedPrice } from 'ecommerce/lib/coupon';
import { holidaySeasonYear } from 'ecommerce/lib/shippingSchedule';
import { formatPrice } from 'utils/format';

import DesignTabs, { DESIGNS_ID } from '../../components/holidayCards/designTabs';
import { HolidayLinkBand, MailStage, UploadLink } from '../../components/holidayCards/greeting';
import {
  FinalCta,
  Hero,
  HowItWorks,
  Pricing,
  Testimonials,
} from '../../components/holidayCards/sections';
import LandingEditorial from '../../components/landing/editorial';
import { ctaTracker, productImage } from '../../components/landing/shared';
import { Head, Layout } from '../../components/layout';
import NewsletterTile from '../../components/newsletter/newsletterTile';
import ProductFaq from '../../components/product/productFaq';
import {
  FOIL_TAB,
  GREETING_CONFIG,
  cardTabs,
  cardsQuery,
  designCount,
  isFoil,
  parseCardsQuery,
  priceTable,
  sharedTiers,
  startingTier,
} from '../../lib/holidayCards.mjs';
import SETTINGS from '../../settings';
import CARDS from '../../specifications/cards';

// Custom Photo Greeting Cards landing page (docs/greeting-cards-page.md). Same concept and
// components as the holiday page, different tone. Title, meta, H1 and the editorial block come
// from content/categories/custom-photo-greeting-cards.md, designs and prices from the catalog,
// the rest is design copy.
const CATEGORY_ID = 'custom-photo-greeting-cards';
const UPLOAD_PORTRAIT_ID = 'photo-card-137';
const UPLOAD_LANDSCAPE_ID = 'photo-card-136';
const track = ctaTracker('greeting_cards_page');

const HERO_IMAGE = productImage('photo-greeting-card/4');
const HERO_SIZES = '(min-width: 992px) 340px, 50vw';

// Stages in design pixels: [x, y, width, rotation]. Panels and the final CTA keep their desktop
// placement on phones, the hero has its own mobile composition.
const PANEL_STAGE = [600, 460];
const PANEL_STAGE_PHONE = [350, 280];

const stamp = (key, at, extra) => ({ kind: 'stamp', image: productImage(key), at, ...extra });
const card = (skuId, at, shape = 'portrait') => ({ kind: 'card', skuId, shape, at });

const HERO_PIECES = [
  stamp('photo-greeting-card/4', [40, 110, 340, -4], {
    phone: [11, 34, 190, -4],
    priority: true,
  }),
  { ...card('photo-card-143', [420, 50, 257, 5]), phone: [220, 8, 127, 5] },
  { ...card('photo-card-140-foil', [383, 350, 247, -3]), phone: [198, 136, 122, -3] },
  { kind: 'postmark', waves: true, deskOnly: true, at: [19, 520, 216, -10] },
];

// Panel copy and stage per tab. `countNoun` completes "7 Thank You designs".
const TAB_COPY = {
  'thank-you': {
    countNoun: 'Thank You designs',
    title: 'Photo thank you cards',
    lead: 'A thank-you lands better with a face on it.',
    body: 'Custom photo thank you cards for the wedding guests, the baby shower crew, the teacher or the birthday party. Pick a design, add the photo from the day and write a few warm words on the back.',
    pieces: [
      stamp('holiday-cards/6', [41, 50, 290, -3]),
      card('photo-card-124', [360, 23, 212, 5]),
      card('photo-card-144', [321, 165, 208, -4]),
    ],
  },
  'save-the-date': {
    countNoun: 'Save the Date designs',
    title: 'Photo save the date cards',
    lead: 'Give them the date before the invitation.',
    body: 'Photo save the date cards for weddings and the big parties in between. Pick a script, a grid or a gold foil design, then add the date, the place and your favorite photo of the two of you.',
    pieces: [
      card('photo-card-141', [22, 70, 217, -6]),
      card('photo-card-139', [220, 30, 204, 3]),
      card('photo-card-134-foil', [290, 250, 276, -3], 'landscape'),
    ],
  },
  'birth-announcements': {
    countNoun: 'Birth Announcements designs',
    title: 'Photo birth announcements',
    lead: 'Introduce the newest member of the family.',
    body: 'Share the name, the birth date and the first photos. Minimal type, a soft lavender welcome or gold foil script, with room on the back for more tiny fingers and toes.',
    pieces: [
      stamp('photo-greeting-card/3', [32, 60, 290, -3]),
      card('photo-card-115', [370, 23, 212, 5]),
      card('photo-card-142', [331, 190, 208, -4]),
    ],
  },
  hello: {
    countNoun: 'Hello designs',
    title: 'Hello photo cards',
    lead: 'No occasion needed.',
    body: "A new address, a family update or a note to friends far away. Fill a grid with this year's snapshots or let one photo do the talking.",
    pieces: [
      stamp('photo-greeting-card/4', [32, 60, 290, -3]),
      card('photo-card-123', [370, 23, 212, 5]),
      card('photo-card-120', [307, 250, 279, -4], 'landscape'),
    ],
  },
  congrats: {
    countNoun: 'Congrats & More designs',
    title: 'Congrats and celebration cards',
    lead: 'For graduations, anniversaries and every hip hip hooray.',
    body: 'Cheers, Yay and Hip Hip Hooray designs for the big days, plus blank layouts that let your photos carry the whole message.',
    pieces: [
      stamp('photo-greeting-card/6', [32, 60, 290, -3]),
      card('photo-card-116', [370, 23, 212, 5]),
      card('photo-card-121', [331, 190, 208, -4]),
    ],
  },
  // The H2 avoids "custom gold foil greeting cards", the /photo-card-134-foil PDP owns that query.
  [FOIL_TAB]: {
    countNoun: 'gold foil designs',
    title: 'A touch of gold foil',
    lead: 'Good news, with a little shine.',
    body: 'Gold foil lettering, from a script save the date to a classic thank-you. The foil is coated in a matte, soft-touch finish',
    pieces: [
      card('photo-card-140-foil', [12, 60, 217, -6]),
      card('photo-card-146-foil', [250, 21, 279, 4], 'landscape'),
      card('photo-card-134-foil', [260, 240, 276, -3], 'landscape'),
    ],
  },
};

// Tile meta uses the cards.js theme, "Other" reads as Congrats.
const themeLabel = (theme) => (theme === 'Other' ? 'Congrats' : theme);

const STEPS = [
  {
    title: 'Pick a design',
    body: 'Browse by occasion, from photo thank you cards to save the dates and birth announcements. Every card is a flat 5×7.',
    image: { imageKey: 'categories/greeting-card', widths: WIDTH_LADDERS.categories },
  },
  {
    title: 'Add your photos and words',
    body: 'Upload from your phone or computer, add names, dates and a message. The back has room for more photos or a longer note at no extra charge.',
    image: productImage('photo-greeting-card/2'),
  },
  {
    title: 'We print and pack the envelopes',
    body: 'Your cards are printed on thick matte paper and arrive with White or Oatmeal envelopes. Add return and recipient addresses on White envelopes for $0.30 a card.',
    image: productImage('photo-greeting-card/8'),
  },
];

// Product facts from content/products/photo-greeting-card.md.
const SPECS = [
  ['Size', 'Flat 5×7 card'],
  ['Paper', 'Thick matte, certified to FSC standards'],
  ['Back side', 'Photos or a message, no extra charge'],
  ['Envelopes', 'White or Oatmeal, one per card'],
  ['Addressing', 'Return and recipient, $0.30 a card, White envelopes only'],
  ['Designs', 'One design per set'],
];

// Testimonial of the photo-greeting-card product content.
const QUOTES = [
  {
    quote: 'Obsessed with our cards from Parabo Press, only wish I had just ordered more.',
    author: '@kenzireddick',
  },
];

// FAQPage JSON-LD source and the rendered accordion. The foil price answer is added only while
// foil and classic designs share their price tiers.
const FAQ = [
  {
    question: 'How long do greeting cards take?',
    // /shipping is the one source for production and delivery times, holiday peak included.
    answer:
      'Production and delivery times, including the holiday peak, are on our <a href="/shipping">shipping page</a>. Shipping cost is calculated at checkout.',
  },
  {
    question: 'How many cards come in a set?',
    answer:
      'A set starts at 20 cards. Add more in groups of 10, and the price per card drops as the set grows.',
  },
  {
    question: 'Do envelopes come with my cards?',
    answer:
      'Yes. Every card comes with an envelope in White or Oatmeal. Envelopes carry Parabo Press branding.',
  },
  {
    question: 'Can you address my envelopes?',
    answer:
      "Yes, on White envelopes. We print your return address and your recipients' addresses for $0.30 a card. Upload your list of recipients and we add them for you.",
  },
  {
    question: 'Can I pick different designs in one set?',
    answer: 'No. All cards in a set use one design.',
  },
  {
    question: 'Can I upload my own card design?',
    answer:
      'Yes. Choose Upload Custom in portrait or landscape and upload a JPG or PNG. Custom foil cards are not available.',
  },
];
const FOIL_PRICE_FAQ = {
  question: 'Do gold foil designs cost more?',
  answer: 'No. Gold foil greeting cards cost the same per card as the other designs.',
};
const FOIL_COLOR_FAQ = {
  question: 'Can I change the color of the foil?',
  answer: 'No. Foil designs come in gold only.',
};

// A card SKU as its own product, so the claimed coupon's restriction is checked against it.
const cardProduct = (sku) => ({
  id: sku.id,
  currency: sku.currency,
  options: [sku],
  bundles: [],
  prices: getSkuProductPrices(sku),
});

// ?theme=Upload Custom lands on the upload block.
const scrollToUpload = () => document.getElementById('upload-your-own')?.scrollIntoView();

const scrollToDesigns = () => {
  const section = document.getElementById(DESIGNS_ID);
  if (section && section.getBoundingClientRect().top < 0) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const GreetingCardsPage = ({ content, query, today }) => {
  const router = useRouter();
  const dataContext = useDataContext();
  const { previewSummary } = useClaimedCoupon();
  const category = dataContext.getCategory(CATEGORY_ID);
  const title = category.heroTitle ?? category.name;
  const hub = dataContext.getCategory('cards');
  const breadcrumb = [
    { name: 'Home', url: '/' },
    { name: hub.name, url: hub.url },
    { name: title },
  ];

  // gather
  const { total, tabs } = useMemo(() => {
    const built = cardTabs(dataContext.skuProducts, CARDS, GREETING_CONFIG);
    const liveIds = new Set(dataContext.skuProducts.map((sku) => sku.id));
    const live = (piece) => piece.kind !== 'card' || liveIds.has(piece.skuId);
    return {
      total: built.total,
      tabs: built.tabs.map((tab) => {
        const { pieces, ...copy } = TAB_COPY[tab.id];
        return {
          ...tab,
          ...copy,
          eyebrow: designCount(tab.designs.length, copy.countNoun),
          stage: (
            <MailStage
              className="gc-stage--panel"
              size={PANEL_STAGE}
              phoneSize={PANEL_STAGE_PHONE}
              pieces={pieces.filter(live)}
            />
          ),
          designs: tab.designs.map((sku) => ({
            sku,
            foil: isFoil(sku.id),
            meta: [themeLabel(CARDS[sku.id].theme), isFoil(sku.id) && 'Gold foil']
              .filter(Boolean)
              .join(' · '),
          })),
        };
      }),
    };
  }, [dataContext.skuProducts]);
  const [initial] = useState(() => parseCardsQuery(query, tabs, GREETING_CONFIG));
  const [selected, setSelected] = useState(initial.tab);

  // Keep the tab in sync with the URL: links with other params keep the page mounted, and Back /
  // Forward onto entries of our own tab updates arrive as shallow route changes. Our own updates
  // parse to the state already set.
  useEffect(() => {
    const handleRouteChange = (url) => {
      const params = new URL(url, window.location.origin).searchParams;
      const next = parseCardsQuery(Object.fromEntries(params), tabs, GREETING_CONFIG);
      setSelected(next.tab);
      if (next.upload) requestAnimationFrame(scrollToUpload);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [router.events, tabs]);

  // normalize: the classic reference SKU for the hero price. The table and the same-price claims
  // only use tiers every live design shares, upload cards included ("Every design").
  const themed = tabs.flatMap((tab) => tab.designs.map((design) => design.sku));
  const classic = themed.find((sku) => !isFoil(sku.id));
  const classicTier = classic && startingTier(classic);
  const familySkus = dataContext.skuProducts.filter(
    (sku) => CARDS[sku.id]?.family === GREETING_CONFIG.family,
  );
  const foilSkus = familySkus.filter((sku) => isFoil(sku.id));
  const everyTiers = sharedTiers(familySkus);
  const classicTiers = sharedTiers(familySkus.filter((sku) => !isFoil(sku.id)));
  const foilTiers = sharedTiers(foilSkus);
  const foilSamePrice = Boolean(everyTiers) && foilSkus.length > 0;
  const showBand = Boolean(holidaySeasonYear(new Date(today)));
  const faq = [...FAQ, ...(foilSamePrice ? [FOIL_PRICE_FAQ] : []), FOIL_COLOR_FAQ];

  // Per-card coupon price in the hero, PriceTag pattern. Tiles show set prices instead.
  const heroDiscount =
    classic && couponSale(previewSummary, cardProduct(classic))?.discountedPriceFrom != null
      ? discountedPrice(previewSummary, classicTier.price, classic.id)
      : null;
  const heroPrice = classicTier && {
    price: formatPrice(heroDiscount ?? classicTier.price, classic.currency),
    originalPrice:
      heroDiscount != null ? formatPrice(classicTier.price, classic.currency) : undefined,
  };

  // One "Every design" row when every design shares its tiers, Classic and Gold foil rows when
  // each finish does, no table otherwise.
  let tableTiers = null;
  if (everyTiers) tableTiers = { classic: everyTiers };
  else if (classicTiers && foilTiers) tableTiers = { classic: classicTiers, foil: foilTiers };
  const priceRows = tableTiers
    ? priceTable(tableTiers).map((row) => ({
        label: row.label,
        classic: formatPrice(row.classic, familySkus[0].currency),
        foil: row.foil != null ? formatPrice(row.foil, familySkus[0].currency) : undefined,
      }))
    : [];
  const priceSeries = everyTiers ? [['Every design', 'classic']] : undefined;

  // The foil price claim only while it holds.
  const panelTabs = tabs.map((tab) =>
    tab.id === FOIL_TAB
      ? {
          ...tab,
          body: `${tab.body}${foilSamePrice ? ' and costs the same as every other design.' : '.'}`,
        }
      : tab,
  );

  // act
  const handleSelect = (tab, location) => {
    track('tab', location, tab)();
    setSelected(tab);
    router.replace(
      {
        pathname: router.pathname,
        query: cardsQuery(router.query, { tab, foilOnly: false }, GREETING_CONFIG),
      },
      undefined,
      { shallow: true, scroll: false },
    );
    if (location === 'final_cta') {
      document.getElementById(DESIGNS_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      scrollToDesigns();
    }
  };

  const tileProps = (design) => ({
    tier: startingTier(design.sku),
    sale: couponSale(previewSummary, cardProduct(design.sku)),
  });

  useEffect(() => {
    if (initial.upload) scrollToUpload();
  }, [initial.upload]);

  return (
    <>
      <Head
        title={category.seoTitle ?? category.name}
        description={category.seoDescription ?? category.shortDescription}
        image={content.seoUrl || getCloudinaryUrl({ imageKey: category.image })}
        heroImage={getCloudinaryUrl({ imageKey: HERO_IMAGE.imageKey, prefix: HERO_IMAGE.prefix })}
        heroImageSrcSet={getCloudinarySrcSet(HERO_IMAGE)}
        heroImageSizes={HERO_SIZES}
      />
      <ScriptMarkupBreadcrumb items={breadcrumb} host={SETTINGS.HOST} />
      <ScriptMarkupFaq items={faq} />

      <Layout>
        <div className="lnd hc hc--greeting">
          {showBand && <HolidayLinkBand onClick={track('holiday_link', 'band')} />}

          <Hero
            title={title}
            breadcrumb={breadcrumb}
            total={total}
            price={heroPrice}
            setSize={classicTier?.quantity}
            eyebrow={`${total} designs for life's moments`}
            eyebrowIcon="mail"
            lead="Turn your favorite photos into greeting cards for the moments in between: a thank-you, a new baby, a save the date or simply hello. Pick a design, add your photos and a few words, and we print your cards with envelopes included."
            media={
              <MailStage
                className="gc-stage--hero"
                size={[720, 700]}
                phoneSize={[390, 300]}
                pieces={HERO_PIECES}
              />
            }
            onChoose={track('choose_design', 'hero')}
          />

          <DesignTabs
            tabs={panelTabs}
            selected={selected}
            foilOnly={false}
            onSelect={handleSelect}
            aside={<UploadLink onClick={track('upload_own', 'tabs')} />}
            ariaLabel="Greeting card designs"
            tileProps={tileProps}
            track={track}
          />

          <HowItWorks
            steps={STEPS}
            title="Design your greeting cards in a few minutes"
            uploadIds={{
              portrait: dataContext.getSkuProduct(UPLOAD_PORTRAIT_ID) ? UPLOAD_PORTRAIT_ID : null,
              landscape: dataContext.getSkuProduct(UPLOAD_LANDSCAPE_ID)
                ? UPLOAD_LANDSCAPE_ID
                : null,
            }}
            track={track}
          />

          {priceRows.length > 0 && (
            <Pricing
              rows={priceRows}
              setSize={classicTier.quantity}
              specs={SPECS}
              image={productImage('photo-greeting-card/7')}
              series={priceSeries}
              intro={
                foilSamePrice
                  ? 'Gold foil designs cost the same as the rest, and envelopes are always included.'
                  : undefined
              }
              note="Price per card. Addressing adds $0.30 a card on White envelopes. Shipping is calculated at checkout."
            />
          )}

          <Testimonials image={productImage('photo-greeting-card/5')} quotes={QUOTES} />

          <section id="faq" className="hc-section hc-faq">
            <div className="container">
              <ProductFaq
                eyebrow="Good to know"
                title="Greeting card questions"
                intro={false}
                items={faq.map(({ question, answer }) => ({
                  caption: question,
                  description: answer,
                }))}
              />
            </div>
          </section>

          <div id="about" className="hc-about">
            <LandingEditorial
              eyebrow="About greeting cards"
              title="Greeting cards from your photos"
              html={content.extraDescription}
            />
          </div>

          <FinalCta
            tabs={tabs.slice(0, 4)}
            eyebrow="Ready when you are"
            script={null}
            title="Say it with a photo"
            body="A thank-you, a welcome or a simple hello. The kind of mail that gets pinned to the fridge."
            media={
              <MailStage
                className="gc-stage--final"
                size={[560, 440]}
                phoneSize={[350, 260]}
                pieces={[
                  stamp('photo-greeting-card/3', [23, 60, 250, -5]),
                  stamp('photo-greeting-card/6', [270, 73, 250, 4]),
                  { kind: 'postmark', at: [355, 20, 142, -12] },
                ]}
              />
            }
            onSelect={handleSelect}
            track={track}
          />

          <NewsletterTile />
        </div>
      </Layout>
    </>
  );
};

GreetingCardsPage.getInitialProps = async ({ query }) => ({
  content: await contentApi.category(CATEGORY_ID),
  query,
  // Dated on the server so the holiday band renders the same on the client.
  today: new Date().toISOString(),
});

GreetingCardsPage.propTypes = {
  content: PropTypes.object.isRequired,
  query: PropTypes.object.isRequired,
  today: PropTypes.string.isRequired,
};

export default GreetingCardsPage;
